import React, { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { useHistory } from 'react-router-dom';
import { FiEdit2, FiMoreVertical, FiPlus, FiTrash2 } from 'react-icons/fi';

import { teamActionTypes } from '@store/Team/Team.actionTypes';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { InitialsBadge, initialsBadgeShapes } from '@/components/InitialsBadge';
import { Button, buttonVariants } from '@/components/Button';
import { Table } from '@/components/Table';
import { Pagination } from '@/components/Pagination';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/Spinner/Spinner.component';
import { ActionsMenu } from '@/components/ActionsMenu';
import { ModalComponent } from '@/components/Modal/Modal.component';
import { ProjectFormModal } from './ProjectFormModal.component';
import { DeleteProjectModal } from './DeleteProjectModal.component';
import styles from './Projects.module.scss';
import { IS_DEMO_MODE } from '@/utils/demoMode';
import { DEMO_PROJECTS } from './Projects.demoData';

const PAGE_SIZE = 10;

function ProjectNameCell({ projectName }) {
  return (
    <div className={styles.projectCell}>
      <InitialsBadge
        label={projectName}
        twoLetter
        shape={initialsBadgeShapes.square}
      />
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Medium}
      >
        {projectName || 'Untitled Project'}
      </Text>
    </div>
  );
}

function TotalTasksCell({ count }) {
  return (
    <div className={styles.taskCountCell}>
      <Text
        variant={textVariantsEnum.Text_md}
        fontWeight={fontWeightEnum.Semibold}
      >
        {count}
      </Text>
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Normal}
        color="gray-500"
      >
        Tasks
      </Text>
    </div>
  );
}

export const ProjectComponent = () => {
  const history = useHistory();
  const teams = useSelector((state) => ({
    loadingProject: state.team.loadingProject,
    projects: state.team.projects,
    active: state.team.active,
  }));
  const auth = useSelector((state) => ({ ...state.auth }));
  const dispatch = useDispatch();

  const [state, setState] = useState({ modal: '', project: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [activeMenuRow, setActiveMenuRow] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [demoProjects, setDemoProjects] = useState(DEMO_PROJECTS);
  const menuRef = useRef(null);

  const { activeCompany } = useSelector((state) => state.auth.profile);

  // Demo mode: no real backend data exists for the demo profile, so
  // GET_PROJECT resolves empty — override at the point of use so the page
  // shows a realistic project list instead of "No Projects Found". Kept as
  // local state (not the static fixture directly) so delete can mutate it.
  const effectiveProjects = IS_DEMO_MODE ? demoProjects : teams.projects;
  const displayLoadingProject = IS_DEMO_MODE ? false : teams.loadingProject;

  React.useEffect(() => {
    if (IS_DEMO_MODE) return;
    if (!isEmpty(teams.active)) {
      dispatch({
        type: teamActionTypes.GET_PROJECT,
        payload: { companyId: activeCompany.companyId },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams.active]);

  const handleCloseEventModal = () => {
    setState({ modal: '', project: null });
  };

  const editProject = (project) => {
    setState({ modal: 'PROJECT-FORM', project });
  };

  const deleteProject = (project) =>
    new Promise((resolve) => {
      dispatch({
        type: teamActionTypes.DELETE_PROJECT,
        payload: {
          ...project,
          teamId: teams.active.activeTeamid,
          companyId: { companyId: activeCompany.companyId },
          onComplete: resolve,
        },
      });
    });

  const handleConfirmDelete = async () => {
    if (deleting || !activeMenuRow) return;
    setDeleting(true);

    // Demo mode: no real project doc exists for the demo profile, so the
    // DELETE_PROJECT dispatch below would fail — remove it from local state
    // instead, mirroring what a successful delete would do.
    if (IS_DEMO_MODE) {
      setDemoProjects((prev) => prev.filter((p) => p.id !== activeMenuRow.id));
      setDeleting(false);
      setShowDeleteModal(false);
      return;
    }

    const result = await deleteProject({ projectId: activeMenuRow.id });
    setDeleting(false);
    if (result?.success) {
      setShowDeleteModal(false);
    }
  };

  const onActionsButtonClick = (e, project) => {
    setActiveMenuRow(project);
    menuRef.current.toggle(e);
  };

  const actionsMenuItems = useMemo(() => {
    if (!activeMenuRow) return [];
    return [
      {
        label: 'Edit',
        icon: <FiEdit2 size={16} />,
        onClick: () => {
          editProject({
            projectId: activeMenuRow.id,
            project_name: activeMenuRow.data?.project_name,
          });
          menuRef.current?.hide();
        },
      },
      {
        label: 'Delete',
        icon: <FiTrash2 size={16} />,
        danger: true,
        onClick: () => {
          setShowDeleteModal(true);
          menuRef.current?.hide();
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMenuRow]);

  const activeProjects = useMemo(
    () =>
      Array.isArray(effectiveProjects)
        ? effectiveProjects.filter((project) => !project?.data?.deleted)
        : [],
    [effectiveProjects]
  );

  const totalPages = Math.max(1, Math.ceil(activeProjects.length / PAGE_SIZE));
  const pagedProjects = activeProjects.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  React.useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const columns = [
    {
      id: 'projectName',
      name: 'Project Name',
      style: { width: '60%' },
      cellRenderer: (row) => (
        <ProjectNameCell projectName={row.data?.project_name} />
      ),
    },
    {
      id: 'totalTasks',
      name: 'Total Tasks',
      style: { width: '30%' },
      cellRenderer: (row) => (
        <TotalTasksCell count={row.data?.taskList?.length || 0} />
      ),
    },
    {
      id: 'actions',
      name: 'Actions',
      style: { width: '10%' },
      thClassName: styles.actionsHeaderCell,
      cellRenderer: (row) => (
        <div className={styles.actionsCell}>
          <Button
            icon={<FiMoreVertical size={14} />}
            variant={buttonVariants.outline}
            ariaLabel="Actions"
            onClick={(e) => onActionsButtonClick(e, row)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className={styles.projectsPage}>
      <ModalComponent
        isVisible={state.modal === 'PROJECT-FORM'}
        onClose={handleCloseEventModal}
      >
        <ProjectFormModal
          project={state.project}
          onClose={handleCloseEventModal}
        />
      </ModalComponent>

      <ModalComponent
        isVisible={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
      >
        <DeleteProjectModal
          projectName={activeMenuRow?.data?.project_name}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
          loading={deleting}
        />
      </ModalComponent>

      <div className={styles.projectsHeaderActions}>
        <Button
          text="Add Project"
          icon={<FiPlus size={14} />}
          variant={buttonVariants.outline}
          onClick={() => setState({ modal: 'PROJECT-FORM', project: null })}
        />
        <Button
          text="Add Task"
          icon={<FiPlus size={14} />}
          variant={buttonVariants.primary}
          onClick={() => history.push('/add-task')}
        />
      </div>

      <div className={styles.projectsContainer}>
        {isEmpty(auth?.profile?.companyList) ? (
          <EmptyState
            illustration
            title="No Company Found"
            description="Please create a company first to create a project."
          />
        ) : displayLoadingProject ? (
          <div className={styles.projectsLoading}>
            <Spinner />
          </div>
        ) : activeProjects.length === 0 ? (
          <EmptyState
            illustration
            title="No Projects Found"
            description="You haven't created any projects yet."
          />
        ) : (
          <>
            <Table
              className={styles.projectsTable}
              data={pagedProjects}
              columns={columns}
            />
            <div className={styles.tableFooter}>
              <Text variant={textVariantsEnum.Text_sm} color="gray-500">
                Showing {(currentPage - 1) * PAGE_SIZE + 1} to{' '}
                {Math.min(currentPage * PAGE_SIZE, activeProjects.length)} of{' '}
                {activeProjects.length} projects
              </Text>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </>
        )}
      </div>

      <ActionsMenu items={actionsMenuItems} ref={menuRef} />
    </div>
  );
};
